import React, { useState, useContext } from 'react';
import { connect } from 'react-redux';

import { useMutation } from '@tanstack/react-query';

import { Button, Form, Select, Radio, Space, Tooltip, Alert, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import keyBy from 'lodash/keyBy';
import Spacer from 'components/ui/Spacer';
import { ApplicationUIContext, DrawerContent } from '@yuhuinc/ui-library';

import { getTranslatedString } from 'helpers/i18n';
import { apiPost } from 'helpers/ApiHelper';
import { CurrentApplicationContext } from '../../ApplicationOverview';

const { Text, Paragraph } = Typography;

const BackgroundCheckErrorsAlerts = ({ errors }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const currentApplication = useContext(CurrentApplicationContext);

  const cotenantsByLeadId = keyBy(currentApplication.cotenants, 'lead_id');

  return Object.entries(cotenantsByLeadId).map(([leadId, cotenant]) => {
    const error = errors[leadId];
    const message = error.details ? (
      <>
        <Text>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.drawerErrorMessage',
            { name: cotenant.full_name }
          )}
        </Text>
        <Paragraph>
          <ul>
            <li>{error.details}</li>
          </ul>
        </Paragraph>
      </>
    ) : (
      error.message
    );

    return <Alert key={leadId} message={message} type="error" showIcon isMobile={isMobile} />;
  });
};

const RunBackgroundCheck = ({
  onSuccess,
  onError,
  applicants,
  creditCheckOptions,
  certnCreditReport,
  certnSoftcheck,
  certnUSCriminalCheck,
}) => {
  const [form] = Form.useForm();
  const { isMobile } = useContext(ApplicationUIContext);

  // TODO: Show errors in the drawer, above the form
  const [runBackgroundCheckErrors, setRunBackgroundCheckErrors] = useState(null);
  const runBackgroundCheckMutation = useMutation({
    mutationKey: ['run_background_check'],
    mutationFn: ({ leadId, ...data }) => {
      return apiPost(
        `/landlords/application_management/leads/${leadId}/run_background_check`,
        data
      );
    },
    onError(error, variables) {
      const { message: errorMessage, details } = error.responseJSON;

      setRunBackgroundCheckErrors((previousValue) => ({
        ...previousValue,
        [variables.leadId]: { message: errorMessage, details },
      }));
    },
  });

  const onRunBackgroundCheck = async (formValues) => {
    setRunBackgroundCheckErrors(null);

    const promises = (formValues?.lead_ids || []).map((leadId) =>
      runBackgroundCheckMutation.mutateAsync({
        leadId,
        credit_check_type: formValues.credit_check_type,
      })
    );

    try {
      await Promise.all(promises);
      onSuccess && onSuccess();
    } catch (error) {
      onError && onError(error);
    }
  };

  const propertyCreditCheckOverride = creditCheckOptions.property_credit_check_permissions;
  let creditReportOptions = [];
  if (certnCreditReport) {
    creditReportOptions.push({
      label: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.credit_check'
      ),
      value: 'credit_check',
    });
  }
  if (
    certnCreditReport &&
    certnSoftcheck &&
    ['company_defaults', 'credit_soft_check'].includes(propertyCreditCheckOverride)
  ) {
    creditReportOptions.push({
      label: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.softcheck'
      ),
      value: 'softcheck',
    });
  }
  if (
    certnCreditReport &&
    certnUSCriminalCheck &&
    ['company_defaults', 'us_criminal_credit_check'].includes(propertyCreditCheckOverride)
  ) {
    creditReportOptions.push({
      label: getTranslatedString(
        'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.us_criminal_check'
      ),
      value: 'us_criminal_check',
    });
  }

  return (
    <DrawerContent>
      {runBackgroundCheckErrors ? (
        <BackgroundCheckErrorsAlerts errors={runBackgroundCheckErrors} />
      ) : null}
      <Form
        form={form}
        layout="vertical"
        size={isMobile && 'large'}
        initialValues={{
          lead_ids: [],
          credit_check_type: creditReportOptions[0]?.value,
        }}
        onFinish={onRunBackgroundCheck}
      >
        <Form.Item
          label={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.selectApplicants'
          )}
          name="lead_ids"
          rules={[
            {
              required: true,
              message: getTranslatedString('required'),
            },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                const firstApplicantWithoutConsent = applicants.find(
                  (applicant) =>
                    value.includes(applicant.lead_id) && !applicant.credit_check_consent
                );

                if (firstApplicantWithoutConsent) {
                  return Promise.reject(
                    getTranslatedString(
                      'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.selectApplicants.errors.notConsent',
                      { name: firstApplicantWithoutConsent.full_name }
                    )
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
        >
          <Select
            allowClear
            style={{
              width: '100%',
            }}
            placeholder={getTranslatedString(
              'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.pleaseSelect'
            )}
            mode="tags"
          >
            {(applicants || [])
              .filter((applicant) => applicant.lead_id)
              .map((applicant) => (
                <Select.Option value={applicant.lead_id} key={applicant.lead_id}>
                  {applicant.full_name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        <Spacer mt={1} />
        <Form.Item
          label={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.selectTypeOfCheck'
          )}
          name="credit_check_type"
        >
          <Radio.Group>
            <Space direction="vertical">
              {creditReportOptions.map((option) => (
                <Radio value={option.value} key={option.value}>
                  <Space size={4} align="center">
                    {option.label}
                    <Tooltip
                      title={getTranslatedString(
                        `landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.selectTypeOfCheck.tooltips.${option.value}`
                      )}
                      placement="right"
                      overlayInnerStyle={{ width: 200 }}
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>
        <Spacer mt={1} />
        <Button
          size={isMobile && 'large'}
          htmlType="submit"
          loading={runBackgroundCheckMutation.isLoading}
        >
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.applicants.backgroundChecks.runCheck'
          )}
        </Button>
      </Form>
    </DrawerContent>
  );
};

const mapStoreToProps = (store) => ({
  certnCreditReport: store.companyDetails.companyPreferences.certn_credit_report,
  certnSoftcheck: store.companyDetails.companyPreferences.certn_softcheck,
  certnUSCriminalCheck: store.companyDetails.companyPreferences.certn_softcheck_us,
});

export default connect(mapStoreToProps, {})(RunBackgroundCheck);
